import { onMounted, ref } from 'vue';

// NOTE: sponsor logos are server-rendered, so the browser starts loading them before Vue attaches
// @error. A missed error event is never replayed, which left broken images on screen for the rest of
// the session (FEP-7379). Failures are therefore caught two ways: the live @error event, and a sweep
// on mount for images that already finished loading with no intrinsic width — those have failed.
//
// Bind all three of these on the logo <img>:
//   v-if="showLogo(sponsor)"
//   :ref="(element) => registerLogo(element, sponsor.sponsor_id)"
//   @error="handleLogoError(sponsor.sponsor_id)"
const useSponsorLogos = () => {
  const failedLogos = ref({});
  const logoElements = new Map();

  const showLogo = (sponsor) =>
    Boolean(sponsor.sponsor_logo) && !failedLogos.value[sponsor.sponsor_id];

  const handleLogoError = (sponsorId) => {
    failedLogos.value[sponsorId] = true;
  };

  const registerLogo = (element, sponsorId) => {
    if (element) {
      logoElements.set(sponsorId, element);
    } else {
      logoElements.delete(sponsorId);
    }
  };

  onMounted(() => {
    for (const [sponsorId, logo] of logoElements) {
      if (!logo.complete || logo.naturalWidth > 0) {
        continue;
      }

      // NOTE: zero intrinsic width does NOT prove failure — uploads accept image/*, and an SVG with
      // no width/height reports 0 in some browsers after loading perfectly well. Confirm with a probe
      // rather than assuming, or we would replace a working logo with the sponsor name. The response
      // is already in cache, so a successful load resolves without another request.
      const probe = new Image();

      probe.addEventListener('error', () => handleLogoError(sponsorId));
      probe.src = logo.currentSrc || logo.src;
    }
  });

  return { showLogo, handleLogoError, registerLogo };
};

export default useSponsorLogos;
